import React, { FC, useEffect } from 'react';
import classnames from 'classnames';
import Svg from 'svg-inline-react';
import { Link, NavLink, matchPath } from 'react-router-dom';
import { Location } from 'history';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import {
  CHATS_ROUTE,
  COMPANY_PROFILE_ROUTE,
  FEATURED_APPLICANTS_ROUTE,
  NEW_FUNNEL_ROUTE,
  OPENINGS_ROUTE,
  OPENING_ROUTE,
  PIPELINE_TALENTS_ROUTE,
  PUBLIC_OPENINGS_ROUTE,
  SETTINGS_TEAM_ROUTE,
} from '@src/Routes/links';
import { toggleNotificationCenterVisibility, fetchNotificationCenter } from '@src/redux/actions/notificationCenter';
import { getNotificationCenterNewCount, isNotificationCenterVisible } from '@src/redux/selectors/notificationCenter';
import FrankLogoNewBlackSvg from '@src/svgs/frank-logo-new-black.svg';
import FrChatDotsBlackSvg from '@src/svgs/fr-chat-dots-black.svg';
import FrGroupSvg from '@src/svgs/fr-group.svg';
import FrAddSvg from '@src/svgs/fr-add.svg';
import FrCheckListSvg from '@src/svgs/fr-checklist.svg';
import FrBriefcaseSvg from '@src/svgs/fr-briefcase.svg';
import FrNotificationCenterSvg from '@src/svgs/fr-notification.svg';
import FrHeartSvg from '@src/svgs/fr-heart.svg';
import FrSettingsSvg from '@src/svgs/fr-settings.svg';
import FrSearchSvg from '@src/svgs/fr-search.svg';
import FrUserSearchSvg from '@src/svgs/fr-user-search.svg';
import FrPieChart from '@src/svgs/fr-pie-chart.svg';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { createLink } from '@src/util/createLink';
import { MessageSenderEnum, useInboxMessageThreadUpdatedSubscription, useSessionQuery } from '@src/graphql/generated';
import { GroupedTalentDictionaryKey } from '@src/containers/Source/constants/groupedTalentDictionary/enums/groupedTalentDictionaryKey.enum';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { COMPENSATION_APP_URL } from '@src/constants';
import { trackEvent } from '@src/metrics';
import { Bubble } from './Bubble';
import { SideMenu } from './SideMenu';
import { QueryParams } from './interfaces/queryParams.interface';
import { BubbleTheme } from './Bubble/enums/bubbleTheme.enum';
import './styles.scss';

export const LeftMenu: FC = () => {
  const { queryParams } = useQueryParams<QueryParams>();

  const sound = document.getElementById('notification') as HTMLAudioElement;

  const isNotificationVisible = useAppSelector(isNotificationCenterVisible);

  const { data } = useSessionQuery();

  const company = data?.company;

  const recruiter = data?.currentRecruiter;

  const unreadNotifications = useAppSelector(getNotificationCenterNewCount);

  const dispatch = useAppDispatch();

  useInboxMessageThreadUpdatedSubscription({
    onSubscriptionData: ({ subscriptionData }) => {
      const messagedRecruiterId = subscriptionData.data?.messageThreadUpdated.updatedMessageThread.recruiter.id;
      const thread = subscriptionData.data?.messageThreadUpdated.updatedMessageThread;
      const application = thread?.application;
      const sender = thread?.messages?.result?.[0]?.sender;

      if (
        sound &&
        recruiter?.id === messagedRecruiterId &&
        !(queryParams.threadId === thread?.id && !document.hidden) &&
        queryParams.applicationId !== application?.id &&
        sender !== MessageSenderEnum.RECRUITER
      ) {
        sound.muted = false;

        sound.play();
      }
    },
  });

  const isActive = (isMatch: unknown, location: Location, path?: string) => {
    const pattern = new RegExp('^' + path);

    return !!location?.isExact || !!(isMatch || (path && pattern.test(location.pathname)));
  };

  const isSavedPageActive = (location: Location<unknown>, path?: string) => {
    const pattern = new RegExp('^' + path);

    return !!(
      path &&
      pattern.test(location.pathname) &&
      queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS
    );
  };

  const companyProfileId = company?.profile.id;
  const companyProfileLogoUrl = company?.profile?.logo?.fullUrl ? company?.profile.logo.fullUrl : undefined;

  const onNavigationItemClick = (interaction: MetricAction): void => {
    if (isNotificationVisible) {
      dispatch(toggleNotificationCenterVisibility());
    }

    trackEvent({
      context: MetricContext.MAIN_NAVIGATION,
      interaction: interaction,
    });
  };

  const unlockAudio = (): void => {
    if (sound) {
      sound.muted = true;

      sound.play();
    }
  };

  useEffect(() => {
    /*
      For Safari auto play doesn't work unless user manually turns it on.
      So we unlock audio and play without voice after user clicks on the page,
      afterwards it will work when notification appears.
     */
    document.body.addEventListener('click', unlockAudio, { once: true });

    dispatch(fetchNotificationCenter());
  }, []);

  return (
    <nav className="left-menu">
      <Link
        className="left-menu__logo"
        onClick={() => {
          trackEvent({
            context: MetricContext.MAIN_NAVIGATION,
            interaction: MetricAction.OPEN_HOME,
          });
        }}
        to={FEATURED_APPLICANTS_ROUTE}
      >
        <Svg src={FrankLogoNewBlackSvg} />
      </Link>

      <NavLink
        activeClassName="left-menu__item--active"
        className="left-menu__item-large"
        exact={false}
        isActive={(match, location) => isActive(match, location)}
        onClick={() => onNavigationItemClick(MetricAction.OPEN_FEATURED_APPLICANTS)}
        to={FEATURED_APPLICANTS_ROUTE}
      >
        <span className="left-menu__item-container">
          <Svg src={FrUserSearchSvg} />
        </span>

        <span className="left-menu__label">Passive search</span>
      </NavLink>

      <NavLink
        activeClassName="left-menu__item--active"
        className="left-menu__item-large"
        exact={false}
        isActive={(_, location) => isSavedPageActive(location, PIPELINE_TALENTS_ROUTE)}
        onClick={() => onNavigationItemClick(MetricAction.OPEN_SAVED_TALENTS)}
        to={createLink(PIPELINE_TALENTS_ROUTE, {
          queryParams: {
            groupedTalentsKey: GroupedTalentDictionaryKey.SAVED_TALENTS,
            pageTitle: 'Saved talents',
          },
        })}
      >
        <span className="left-menu__item-container">
          <Svg src={FrHeartSvg} />
        </span>

        <span className="left-menu__label">Saved talents</span>
      </NavLink>

      <div className="left-menu__frame">
        <NavLink
          activeClassName="left-menu__item--active"
          className="left-menu__item"
          exact={false}
          isActive={(match, location) =>
            !!isActive(match, location) || !!matchPath(location.pathname, { path: PUBLIC_OPENINGS_ROUTE })
          }
          onClick={() => onNavigationItemClick(MetricAction.OPEN_PUBLIC_OPENINGS)}
          to={createLink(PUBLIC_OPENINGS_ROUTE)}
        >
          <span className="left-menu__item-container">
            <Svg src={FrSearchSvg} />
          </span>

          <span className="left-menu__label">Jobs</span>
        </NavLink>

        <NavLink
          activeClassName="left-menu__item--active"
          className="left-menu__item"
          exact={false}
          isActive={(match, location) =>
            !!isActive(match, location) || !!matchPath(location.pathname, { path: OPENING_ROUTE })
          }
          onClick={() => onNavigationItemClick(MetricAction.OPEN_OPENINGS)}
          to={createLink(OPENINGS_ROUTE)}
        >
          <span className="left-menu__item-container">
            <Svg src={FrCheckListSvg} />
          </span>

          <span className="left-menu__label">Your jobs</span>
        </NavLink>

        <NavLink
          activeClassName="left-menu__item--active"
          className="left-menu__item left-menu__item-add-a-job"
          exact={false}
          isActive={(match, location) =>
            !!isActive(match, location) || !!matchPath(location.pathname, { path: NEW_FUNNEL_ROUTE })
          }
          onClick={() => onNavigationItemClick(MetricAction.OPEN_ADD_OPENING)}
          to={createLink(NEW_FUNNEL_ROUTE)}
        >
          <span className="left-menu__item-container">
            <Svg className="left-menu__item-container-add-svg" src={FrAddSvg} />
          </span>

          <span className="left-menu__label">Add a job</span>
        </NavLink>
      </div>

      <NavLink
        activeClassName="left-menu__item--active"
        className="left-menu__item"
        exact={false}
        isActive={(match, location) => isActive(match, location)}
        onClick={() => onNavigationItemClick(MetricAction.OPEN_CHATS)}
        to={createLink(CHATS_ROUTE)}
      >
        <span className="left-menu__item-container">
          <Svg src={FrChatDotsBlackSvg} />
        </span>

        <span className="left-menu__label">Chats</span>
      </NavLink>

      <a
        className="left-menu__item"
        href={COMPENSATION_APP_URL}
        onClick={() =>
          trackEvent({
            context: MetricContext.MAIN_NAVIGATION,
            interaction: MetricAction.OPEN_COMPENSATION_APP,
          })
        }
        target="_blank"
        rel="noreferrer"
      >
        <span className="left-menu__item-container">
          <Svg src={FrPieChart} />

          <Bubble className="left-menu__item-bubble left-menu__item-bubble--new" theme={BubbleTheme.BLUE}>
            new
          </Bubble>
        </span>

        <span className="left-menu__label">Salaries</span>
      </a>

      <div
        className={classnames(
          'left-menu__item',
          'left-menu__item--bottom',
          isNotificationVisible && 'left-menu__item--active',
        )}
        onClick={() => {
          dispatch(toggleNotificationCenterVisibility());

          if (!isNotificationVisible) {
            trackEvent({
              context: MetricContext.MAIN_NAVIGATION,
              interaction: MetricAction.OPEN_NOTIFICATION_CENTER,
            });
          }
        }}
      >
        <span className="left-menu__item-container">
          <Svg src={FrNotificationCenterSvg} />
          {unreadNotifications > 0 && (
            <Bubble className="left-menu__item-bubble" theme={BubbleTheme.RED}>
              {unreadNotifications}
            </Bubble>
          )}
        </span>
      </div>

      <NavLink
        activeClassName="left-menu__item--active"
        className="left-menu__item"
        exact={false}
        isActive={(match, location) => isActive(match, location)}
        onClick={() => onNavigationItemClick(MetricAction.OPEN_TEAM)}
        to={SETTINGS_TEAM_ROUTE}
      >
        <span className="left-menu__item-container">
          <Svg className="left-menu__icon-black" src={FrGroupSvg} />
        </span>

        <span className="left-menu__label">Team</span>
      </NavLink>

      <NavLink
        activeClassName="left-menu__item--active"
        className="left-menu__item"
        exact={false}
        isActive={(match, location) => isActive(match, location)}
        onClick={() => onNavigationItemClick(MetricAction.OPEN_COMPANY)}
        to={createLink(COMPANY_PROFILE_ROUTE, {
          params: { companyProfileId },
        })}
      >
        <span className="left-menu__item-container">
          {companyProfileLogoUrl ? <img src={companyProfileLogoUrl} /> : <Svg src={FrBriefcaseSvg} />}
        </span>

        <span className="left-menu__label">Company</span>
      </NavLink>

      {recruiter?.isSiteAdmin && (
        <a className="left-menu__item" href="/admin/companies" target="_">
          <span className="left-menu__item-container">
            <Svg src={FrSettingsSvg} />
          </span>

          <span className="left-menu__label">Admin</span>
        </a>
      )}

      <div className="left-menu__item left-menu__item--square left-menu__item--no-opacity">
        <SideMenu />
      </div>
    </nav>
  );
};
